import React from "react";
// Meters

// Default
import { useState, useEffect, useContext } from "react";
import Swal from "sweetalert2";
// import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";

// Custom
import Layout from "../../layout";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid2";
import Divider from "@mui/material/Divider";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";
import CustomInput from "../../forms/input/Input";
import CustomSearch from "../search/Search";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomToast from "../../components/toast/CustomToast";
// import CustomSwitch from "../switch/Switch";
import CustomChip from "../../components/chip/Chip";
import AddCustomFieldsDrawer from "../drawer/AddCustomFieldsDrawer";
import CustomFieldsModal from "../modals/CustomFieldsModal";

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";
import { CustomFieldsContext } from "../../context/CustomFieldsProvider";
import {
  DEFAULT_METER_FIELDS,
  getTypeSpecificFields,
  METER_TYPE_IDS,
} from "../../utils/meterFieldsConfig";

// Redux
import { ToastState } from "../../types";
import { useAppSelector } from "../../redux/hooks";

// Types
interface Customer {
  id: string;
  name: string;
  value: string;
  label: string;
  [key: string]: any;
}

interface RegisterTypeOption {
  label: string;
  value: string;
}

interface MeterInfo {
  id: string | { id: string };
  manufacturer: string | { value: string; label: string };
  meterType: string | { value: string; label: string };
  meterSerialNo: string;
  meterBadgeNo: string;
  meterInstallDate: string | Date;
  useAsLoadProfile: boolean;
  status: string;
  assignToCustomer: string | { value: string; label: string };
  siteId: string;
  existingMeterNumber: string;
  lineLtCtRatio: string;
  meterBoxDimensions: string;
  meterLegalStatus: string;
  remarks: string;
  [key: string]: any;
}

interface ReadClusterDetail {
  startDate: string | Date;
  endDate: Date;
  registerTypeCode: {
    label: string;
    value: string;
  };
}

interface RootState {
  auth: {
    customFieldId: string;
  };
}

interface CustomFieldsContextType {
  toggleCustomFieldDrawer: (type: string, isOpen: boolean) => void;
  createdFields: Record<string, any[]>;
  setCreatedFields: (fields: Record<string, any[]>) => void;
}

interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
      error?: string;
      details?: string;
    };
  };
  message?: string;
}

// a5b9f650-4112-11f0-a9a0-9fd706385992 (gas) || 1a854aa0-411f-11f0-a9a0-9fd706385992 (water) || 577adaf0-4125-11f0-a9a0-9fd706385992 (electricity) -- test meter id

// 1e57a780-4742-11f0-9e90-2d1ff1f84e54 (electricity) with config from meterType page
// 53376bc0-4742-11f0-9e90-2d1ff1f84e54 with only required details

const Meters: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState("Meters");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [registerTypeCodeOptions, setRegisterTypeCodeOptions] = useState<
    RegisterTypeOption[]
  >([]);
  // const [counter, setCounter] = useState(1);
  const [names, setNames] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [readClusterDetail, setReadClusterDetail] = useState<ReadClusterDetail>(
    {
      startDate: "",
      endDate: new Date(),
      registerTypeCode: { label: "KWH", value: "IP_KWH" },
    },
  );
  const [meterInfo, setMeterInfo] = useState<MeterInfo>({
    id: "",
    manufacturer: "",
    meterType: "",
    meterSerialNo: "",
    meterBadgeNo: "",
    meterInstallDate: "",
    useAsLoadProfile: false,
    status: "",
    assignToCustomer: "",
    siteId: "",
    existingMeterNumber: "",
    lineLtCtRatio: "",
    meterBoxDimensions: "",
    meterLegalStatus: "",
    remarks: "",
  });
  const [dynamicTypeSpecificFields, setDynamicTypeSpecificFields] = useState<
    any[]
  >([]);

  const navigate = useNavigate();
  const context = useContext(
    CustomFieldsContext,
  ) as CustomFieldsContextType | null;
  const { toggleCustomFieldDrawer, createdFields, setCreatedFields } =
    context || {
      toggleCustomFieldDrawer: () => {},
      createdFields: {},
      setCreatedFields: () => {},
    };
  const location = useLocation();
  const [chipStatus, setChipStatus] = useState("Unknown");

  const { customFieldId } = useAppSelector((state: RootState) => state.auth);

  const chipColor = (status: string): string => {
    if (status === "Connected") return "#9CE976";
    if (status === "Disconnected") return "#FF3B30";
    if (status === "Unknown") return "#b6b6b6";
    return "#b6b6b6";
  };

  const OPTIONS = [
    { label: "MeterTime", value: "IP_MeterTime" },
    { label: "Current", value: "IP_Current" },
    { label: "Voltage", value: "IP_Voltage" },
    { label: "Frequency", value: "IP_Frequency" },
    { label: "NeutralCurrent", value: "IP_NeutralCurrent" },
    { label: "PowerFactor", value: "IP_PowerFactor" },
    { label: "KW", value: "IP_KW" },
    { label: "KVA", value: "IP_KVA" },
    { label: "KWH", value: "IP_KWH" },
    { label: "KWHEXP", value: "IP_KWHEXP" },
    { label: "KVAH", value: "IP_KVAH" },
    { label: "KVAHEXP", value: "IP_KVAHEXP" },
    { label: "MDKW", value: "IP_MDKW" },
    { label: "MDKVA", value: "IP_MDKVA" },
    { label: "MDResetCount", value: "IP_MDResetCount" },
    { label: "ProgramCount", value: "IP_ProgramCount" },
    { label: "TamperCount", value: "IP_TamperCount" },
    { label: "TotalPowerOnDuration", value: "IP_TotalPowerOnDuration" },
    { label: "RelayStatus", value: "IP_RelayStatus" },
    { label: "LoadLimitValue", value: "IP_LoadLimitValue" },
  ];

  const TABS = ["Meters", "Read Cluster"];

  const meterTypeOptions = [
    {
      value: "SLS-E1-DLMS-1P",
      label: "SLS-E1-DLMS-1P",
    },
    {
      value: "SLS-E2-DLMS-3P",
      label: "SLS-E2-DLMS-3P",
    },
    {
      value: "ITRON-G1-CGT-01",
      label: "ITRON-G1-CGT-01",
    },
    {
      value: "HW-G3-U16",
      label: "HW-G3-U16",
    },
    {
      value: "electricity",
      label: "Electricity",
      typeId: METER_TYPE_IDS.electricity,
    },
    {
      value: "water",
      label: "Water",
      typeId: METER_TYPE_IDS.water,
    },
    {
      value: "gas",
      label: "Gas",
      typeId: METER_TYPE_IDS.gas,
    },
  ];

  const Manufacturer_options = [
    { value: "SENSUS", label: "SENSUS" },
    { value: "ITRON", label: "ITRON" },
  ];

  const handleResetMeterInfo = () => {
    setMeterInfo({
      id: "",
      manufacturer: "",
      meterType: "",
      meterSerialNo: "",
      meterBadgeNo: "",
      meterInstallDate: "",
      useAsLoadProfile: false,
      status: "Inactive",
      assignToCustomer: "",
      siteId: "",
      existingMeterNumber: "",
      lineLtCtRatio: "",
      meterBoxDimensions: "",
      meterLegalStatus: "",
      remarks: "",
    });
    setDynamicTypeSpecificFields([]);
    setSearchValue("");
  };

  const generateUniqueName = (): string => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000);
    const newName = `meter_${timestamp}_${randomNum}`;
    setNames([...names, newName]);
    // setCounter(Math.random());
    return newName;
  };

  // Get default fields with populated options
  const getPopulatedDefaultFields = () => {
    return DEFAULT_METER_FIELDS.map((field) => {
      if (field.name === "meterType") {
        return {
          ...field,
          options: meterTypeOptions,
          onChange: handleMeterTypeChange,
        };
      } else if (field.name === "assignToCustomer") {
        return {
          ...field,
          options: customers,
        };
      } else if (field.name === "manufacturer") {
        return {
          ...field,
          options: Manufacturer_options,
        };
      }
      return field;
    });
  };

  // Universal form field renderer for both default and type-specific fields
  const renderFormField = (field: any) => {
    const value = meterInfo[field.name] || "";
    const isRequired = field.required;

    switch (field.type) {
      case "text":
        return (
          <CustomInput
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type="text"
            value={value}
            required={isRequired}
            onChange={(event) => {
              setMeterInfo({
                ...meterInfo,
                [field.name]: event.target.value,
              });
            }}
          />
        );

      case "number":
        return (
          <CustomInput
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type="number"
            value={value}
            required={isRequired}
            onChange={(event) => {
              setMeterInfo({
                ...meterInfo,
                [field.name]: event.target.value,
              });
            }}
          />
        );

      case "dropdown":
        return (
          <CustomOutlinedSelect
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            options={field.options}
            value={value}
            required={isRequired}
            onChange={
              field.onChange ||
              ((selectedValue) => {
                setMeterInfo({
                  ...meterInfo,
                  [field.name]: selectedValue,
                });
              })
            }
          />
        );

      case "date":
        return (
          <DatePickerInput
            key={field.name}
            label={field.label}
            format={"dd/MM/yyyy"}
            placeholder={field.placeholder || "dd/mm/yyyy"}
            value={value ? new Date(value) : null}
            required={isRequired}
            onChange={(selectedDate) => {
              setMeterInfo({
                ...meterInfo,
                [field.name]: selectedDate,
              });
            }}
          />
        );

      case "checkbox":
        return (
          <Stack key={field.name} mt={"25px"}>
            <FormControlLabel
              key={field.name}
              control={
                <Checkbox
                  checked={value || false}
                  required={isRequired}
                  onChange={(event) => {
                    setMeterInfo({
                      ...meterInfo,
                      [field.name]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={field.label}
            />
          </Stack>
        );

      default:
        return (
          <CustomInput
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type="text"
            value={value}
            required={isRequired}
            onChange={(event) => {
              setMeterInfo({
                ...meterInfo,
                [field.name]: event.target.value,
              });
            }}
          />
        );
    }
  };

  // Keep the old function name for type-specific fields for backward compatibility
  const renderTypeSpecificField = (field: any) => {
    return renderFormField(field);
  };

  // Handle meter type change to load type-specific fields
  const handleMeterTypeChange = async (selectedMeterType: any) => {
    setMeterInfo({
      ...meterInfo,
      meterType: selectedMeterType,
    });

    if (selectedMeterType?.typeId) {
      try {
        // Fetch type-specific data from asset endpoint using typeId
        const response = await fetchGETRequest(
          `asset/${selectedMeterType.typeId}`,
        );

        if (response?.additionalInfo) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );

          // Update meterInfo with fetched data and set type-specific fields
          const updatedMeterInfo: any = {
            ...meterInfo,
            meterType: selectedMeterType,
          };

          typeSpecificFields.forEach((field: any) => {
            if (response.additionalInfo.hasOwnProperty(field.name)) {
              const apiValue = response.additionalInfo[field.name];
              if (field.type === "dropdown" && field.options) {
                updatedMeterInfo[field.name] =
                  field.options.find((opt: any) => opt.value === apiValue) ||
                  null;
              } else if (field.type === "date" && apiValue) {
                updatedMeterInfo[field.name] = new Date(apiValue);
              } else {
                updatedMeterInfo[field.name] = apiValue;
              }
            }
          });

          setMeterInfo(updatedMeterInfo);
          setDynamicTypeSpecificFields(typeSpecificFields);
        } else {
          // If no API data, just set the fields without defaults
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );
          setDynamicTypeSpecificFields(typeSpecificFields);
        }
      } catch (error) {
        console.error("Error fetching meter type data:", error);
        // If error, just set the fields without defaults
        const typeSpecificFields = getTypeSpecificFields(
          selectedMeterType.value.toLowerCase(),
        );
        setDynamicTypeSpecificFields(typeSpecificFields);
      }
    } else {
      // For existing meter types without typeId, just set type-specific fields
      if (selectedMeterType?.value) {
        const typeSpecificFields = getTypeSpecificFields(
          selectedMeterType.value.toLowerCase(),
        );
        setDynamicTypeSpecificFields(typeSpecificFields);
      } else {
        setDynamicTypeSpecificFields([]);
      }
    }
  };

  const handleSearch = async (payload?: any) => {
    if (!searchValue.trim() && !payload) {
      setToast({
        message: "Please enter a valid search value.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetchGETRequest(
        `device/${searchValue || payload}`,
        {},
        true,
      );

      const connectionResponse = await fetchGETRequest(
        `devices/${searchValue || payload}/connectionstatus`,
        {},
        true,
      );

      if (
        connectionResponse?.data?.Status?.trim().toUpperCase() ===
        "DISCONNECT SUCCESS"
      ) {
        setChipStatus("Disconnected");
      } else {
        setChipStatus("Connected");
      }

      if (response?.data?.additionalInfo) {
        const {
          InstallationDate,
          UseAsLoadProfile,
          meterCommodityType,
          manufacturer,
          status,
          SerialNumber,
          BadgeNumber,
          siteId,
          existingMeterNumber,
          lineLtCtRatio,
          meterBoxDimensions,
          meterLegalStatus,
          remarks,
        } = response?.data?.additionalInfo;

        const selectedMeterType = meterTypeOptions.find(
          (item) => item.value === meterCommodityType,
        );

        if (selectedMeterType?.value) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );
          setDynamicTypeSpecificFields(typeSpecificFields);
        }

        setMeterInfo({
          manufacturer:
            Manufacturer_options.find((item) => item.value === manufacturer) ||
            "",
          meterType: selectedMeterType || "",
          meterSerialNo: SerialNumber,
          meterBadgeNo: BadgeNumber,
          meterInstallDate: InstallationDate ? new Date(InstallationDate) : "",
          useAsLoadProfile: UseAsLoadProfile || false,
          status: status || "Inactive",
          siteId: siteId || "",
          existingMeterNumber: existingMeterNumber || "",
          lineLtCtRatio: lineLtCtRatio || "",
          meterBoxDimensions: meterBoxDimensions || "",
          meterLegalStatus: meterLegalStatus || "",
          remarks: remarks || "",
          assignToCustomer:
            customers?.find(
              (item) => item.value === response?.data?.customerId?.id,
            ) || "",
          id: response?.data?.id,
        });

        if (selectedMeterType?.value) {
          const typeSpecificFields = getTypeSpecificFields(
            selectedMeterType.value.toLowerCase(),
          );
          typeSpecificFields.forEach((field: any) => {
            if (response?.data?.additionalInfo.hasOwnProperty(field.name)) {
              const apiValue = response.data.additionalInfo[field.name];
              if (field.type === "dropdown" && field.options) {
                setMeterInfo((prev) => ({
                  ...prev,
                  [field.name]:
                    field.options.find(
                      (option: any) => option.value === apiValue,
                    ) || null,
                }));
              } else if (field.type === "date" && apiValue) {
                setMeterInfo((prev) => ({
                  ...prev,
                  [field.name]: new Date(apiValue),
                }));
              } else {
                setMeterInfo((prev) => ({
                  ...prev,
                  [field.name]: apiValue,
                }));
              }
            }
          });
        }
      } else {
        setToast({
          message: "Meter not found",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error searching meter:", error);
      setToast({
        message: "Error searching meter",
        severity: "error",
      });
    }
  };

  const handleSave = async () => {
    // Dynamic validation based on JSON configuration
    const populatedFields = getPopulatedDefaultFields();
    const requiredFields = populatedFields.filter(
      (field: any) => field.required,
    );

    for (const field of requiredFields) {
      const fieldValue = meterInfo[field.name];

      // Special handling for dropdown fields (objects with value property)
      if (field.type === "dropdown") {
        if (!fieldValue || !fieldValue.value || fieldValue.value === "") {
          setToast({
            message: `${field.label} is required`,
            severity: "error",
          });
          return;
        }
      } else {
        // Regular validation for other field types
        if (!fieldValue || fieldValue === "") {
          setToast({
            message: `${field.label} is required`,
            severity: "error",
          });
          return;
        }
      }
    }

    try {
      const meterTypeValue =
        typeof meterInfo.meterType === "object"
          ? meterInfo.meterType.value
          : meterInfo.meterType;
      const manufacturerValue =
        typeof meterInfo.manufacturer === "object"
          ? meterInfo.manufacturer.value
          : meterInfo.manufacturer;
      const assignToCustomerValue =
        typeof meterInfo.assignToCustomer === "object"
          ? meterInfo.assignToCustomer.value
          : meterInfo.assignToCustomer;

      const requestData: any = {
        tenantId: {
          id: "e936e050-e1d7-11ee-8baa-37d11d926d57",
          entityType: "TENANT",
        },
        name: generateUniqueName(),
        type: "METER",
        label: "NY Building",
        additionalInfo: {
          registerData: [],
          // UseAsLoadProfile: false,
          meterCommodityType: meterTypeValue || "",
          manufacturer: manufacturerValue || "",
          SerialNumber: meterInfo?.meterSerialNo || "",
          BadgeNumber: meterInfo?.meterBadgeNo || "",
          InstallationDate: meterInfo?.meterInstallDate
            ? formatDate(meterInfo?.meterInstallDate, "YYYY-MM-DD")
            : formatDate(new Date(), "YYYY-MM-DD"),
          UseAsLoadProfile: Boolean(meterInfo?.useAsLoadProfile),
          status: meterInfo?.status || "Inactive",
          siteId: meterInfo?.siteId || "",
          existingMeterNumber: meterInfo?.existingMeterNumber || "",
          lineLtCtRatio: meterInfo?.lineLtCtRatio || "",
          meterBoxDimensions: meterInfo?.meterBoxDimensions || "",
          meterLegalStatus: meterInfo?.meterLegalStatus || "",
          remarks: meterInfo?.remarks || "",
          ...dynamicTypeSpecificFields.reduce((acc: any, field: any) => {
            const value = meterInfo[field.name];
            console.log(
              `Processing field ${field.name}:`,
              value,
              `Type: ${field.type}`,
            );

            if (value !== undefined && value !== null && value !== "") {
              if (field.type === "dropdown" && value?.value) {
                acc[field.name] = value.value;
              } else if (field.type === "date" && value) {
                try {
                  acc[field.name] = formatDate(value, "YYYY-MM-DD");
                } catch (dateError) {
                  console.error(
                    `Error formatting date for field ${field.name}:`,
                    dateError,
                  );
                  acc[field.name] = null;
                }
              } else if (field.type === "checkbox") {
                acc[field.name] = Boolean(value);
              } else if (field.type === "number") {
                acc[field.name] = Number(value) || 0;
              } else {
                acc[field.name] = String(value);
              }
            }
            return acc;
          }, {}),
        },
      };

      // Only add ID if it's an update (existing meter)
      const meterId =
        typeof meterInfo.id === "object" ? meterInfo.id.id : meterInfo.id;
      if (meterId) {
        requestData.id = {
          id: meterId,
          entityType: "DEVICE",
        };
      }

      // Only add customer assignment if selected
      if (assignToCustomerValue) {
        requestData.customerId = {
          id: assignToCustomerValue,
          entityType: "CUSTOMER",
        };
      }

      console.log(
        "Sending request data:",
        JSON.stringify(requestData, null, 2),
      );

      // Additional validation before sending
      console.log("Current meterInfo state:", meterInfo);
      console.log("Dynamic type-specific fields:", dynamicTypeSpecificFields);

      // Validate critical fields
      if (!requestData.additionalInfo.meterCommodityType) {
        throw new Error("Meter  type is missing");
      }
      if (!requestData.additionalInfo.manufacturer) {
        throw new Error("Manufacturer is missing");
      }
      if (!requestData.additionalInfo.siteId) {
        throw new Error("Site ID is missing");
      }

      const response = await fetchPOSTRequest(`device`, requestData);

      console.log("Response received:", response);

      if (response?.id?.id) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Meter ${response.id.id} ${
            meterInfo?.id ? "updated" : "created"
          } successfully!`,
        }).then(() => {
          handleResetMeterInfo();
        });
      } else {
        throw new Error("No response ID received from server");
      }
    } catch (error) {
      const apiError = error as ApiError;
      console.error("Error during save request:", apiError);
      console.error("Error response data:", apiError?.response?.data);
      console.error("Error response status:", apiError?.response?.status);

      let errorMessage = "Error during save request";

      if (apiError?.response?.status === 400) {
        errorMessage = "Bad Request: Please check all required fields";

        // Log detailed error information for debugging
        if (apiError?.response?.data) {
          console.error("Server validation errors:", apiError.response.data);

          // Try to extract more specific error message
          if (apiError.response.data.message) {
            errorMessage = `Bad Request: ${apiError.response.data.message}`;
          } else if (apiError.response.data.error) {
            errorMessage = `Bad Request: ${apiError.response.data.error}`;
          } else if (apiError.response.data.details) {
            errorMessage = `Bad Request: ${apiError.response.data.details}`;
          }
        }
      } else if (apiError?.response?.status === 401) {
        errorMessage = "Unauthorized: Please check your authentication";
      } else if (apiError?.response?.status === 403) {
        errorMessage = "Forbidden: You don't have permission to create meters";
      } else if (apiError?.response?.status === 500) {
        errorMessage = "Server Error: Please try again later";
      } else if (apiError?.response?.data?.message) {
        errorMessage = apiError.response.data.message;
      } else if (apiError?.message) {
        errorMessage = apiError.message;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    }
  };

  useEffect(() => {
    // fetchMeterTypeOptionsData();
    setRegisterTypeCodeOptions(OPTIONS);
    fetchCustomers();
    generateUniqueName();
  }, []);

  // Effect to handle search when customers are loaded and location state exists
  useEffect(() => {
    if (location?.state?.meterid && customers.length > 0) {
      setSearchValue(location?.state?.meterid);
      handleSearch(location?.state?.meterid);
    }
  }, [location, customers]);

  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await fetchGETRequest(
          `asset/${customFieldId}`,
          {},
          true,
        );

        if (response?.data?.additionalInfo) {
          const { customFields } = response?.data?.additionalInfo;
          setCreatedFields(customFields);
        }
      } catch (error) {
        const apiError = error as ApiError;
        console.error("Error fetching consumer:", apiError.message);
      }
    };
    fetchCustomFields();
  }, [customFieldId, setCreatedFields]);

  const fetchCustomers = async () => {
    try {
      const customerResponse = await fetchGETRequest(
        `customers?pageSize=10&page=0`,
        {},
      );
      const transformedArray =
        customerResponse?.data?.map((customer: any) => ({
          label: customer.name,
          value: customer.id.id,
        })) || [];

      setCustomers(transformedArray);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  const getCustomFields = (item: any) => {
    switch (item?.type?.label) {
      case "Date":
        return (
          <DatePickerInput
            label={item?.title}
            format={"dd/MM/yyyy"}
            placeholder={"dd/mm/yyyy"}
            value={meterInfo?.[item?.title?.toUpperCase()] || ""}
            onChange={(event) => {
              setMeterInfo({
                ...meterInfo,
                [item?.title?.toUpperCase()]: event,
              });
            }}
          />
        );
      case "Checkbox":
        return (
          <Stack mt={"30px"}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={meterInfo?.[item?.title?.toUpperCase()] || false}
                  onChange={(event) => {
                    setMeterInfo({
                      ...meterInfo,
                      [item?.title?.toUpperCase()]: event.target.checked,
                    });
                  }}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": {
                      color: "#1976d2",
                    },
                  }}
                />
              }
              label={item?.title}
            />
          </Stack>
        );
      case "Text":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="text"
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Number":
        return (
          <>
            <CustomInput
              label={item?.title}
              placeholder={item?.title}
              type="number"
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event.target.value,
                });
              }}
            />
          </>
        );

      case "Dropdown":
        return (
          <>
            <CustomOutlinedSelect
              label={item?.title}
              placeholder={item?.title}
              options={item?.options}
              value={meterInfo?.[item?.title?.toUpperCase()] || ""}
              onChange={(event) => {
                setMeterInfo({
                  ...meterInfo,
                  [item?.title?.toUpperCase()]: event,
                });
              }}
            />
          </>
        );

      default:
        return null;
    }
  };

  const handleTabChange = (_: any, newValue: string) => {
    setSelectedTab(newValue);
  };

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />

      <Card
        className="meters_search_card"
        sx={{
          overflow: "visible",
          position: "relative",
        }}
      >
        <Typography variant="h5">Meter Information</Typography>
        <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
          <Typography variant="body1" fontWeight={600}>
            Meter ID:
          </Typography>
          <CustomSearch
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={"Search Meter.."}
            sx={{
              width: "500px",
              height: "40px",
            }}
            onClick={() => handleSearch()}
          />

          <ContainedButton
            label={"Save"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              height: "35px",
              color: "#ffffff",
            }}
            onClick={handleSave}
            disabled={
              !meterInfo?.siteId ||
              !meterInfo?.meterType ||
              !meterInfo?.manufacturer
            }
          />
        </Stack>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <TabContext value={selectedTab}>
          <CardHeader
            sx={{ paddingBottom: 0 }}
            title={
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <TabList
                  onChange={handleTabChange}
                  variant={"scrollable"}
                  scrollButtons={false}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: "##1280E7",
                      height: "3px",
                    },
                  }}
                >
                  {TABS.map((tab, index) => {
                    return (
                      <Tab
                        label={tab}
                        value={tab}
                        key={index}
                        className="consumer_right_card_list_tab"
                      />
                    );
                  })}
                </TabList>

                <ContainedButton
                  label={"Create custom fields"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={() => toggleCustomFieldDrawer("Meter", true)}
                />
              </Stack>
            }
          />
          <Divider />

          <CardContent>
            <TabPanel value={"Meters"} sx={{ padding: 0 }}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Stack direction={"row"} alignItems={"center"} gap={"4px"}>
                  <Typography variant="body1">Status:</Typography>
                  <CustomChip
                    label={chipStatus}
                    color="#fff"
                    backgroundColor={chipColor(chipStatus)}
                  />
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                {/* Render Default Fields from JSON Configuration */}
                {getPopulatedDefaultFields().map((field: any) => (
                  <Grid
                    key={field.name}
                    size={{
                      xs: 12,
                      md: field.fullWidth ? 12 : 4,
                      lg: field.fullWidth ? 12 : 4,
                    }}
                    mt={"10px"}
                  >
                    {renderFormField(field)}
                  </Grid>
                ))}
              </Grid>

              {/* Type-Specific Fields Section */}
              {meterInfo.meterType && dynamicTypeSpecificFields.length > 0 && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        variant="h6"
                        sx={{ color: "#1976d2", fontWeight: 600 }}
                      >
                        {typeof meterInfo.meterType === "object"
                          ? meterInfo.meterType.label
                          : meterInfo.meterType}{" "}
                        Specific Fields
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", marginBottom: "10px" }}
                      >
                        These fields are specific to{" "}
                        {typeof meterInfo.meterType === "object"
                          ? meterInfo.meterType.label
                          : meterInfo.meterType}{" "}
                        meter types
                      </Typography>
                    </Grid>

                    {/* Render type-specific fields */}
                    {dynamicTypeSpecificFields.map((field) => (
                      <Grid
                        key={field.name}
                        size={{ xs: 12, md: 4, lg: 4 }}
                        mt={"10px"}
                      >
                        {renderTypeSpecificField(field)}
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}

              {createdFields?.["Meter"] && (
                <>
                  <Stack sx={{ paddingTop: "30px", paddingBottom: "20px" }}>
                    <Divider sx={{ borderWidth: "1.5px" }} />
                  </Stack>

                  <Grid container spacing={2}>
                    {createdFields?.["Meter"]?.map((item: any) => {
                      return (
                        <Grid size={{ xs: 12, md: 4, lg: 4 }} mt={"10px"}>
                          {getCustomFields(item)}
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </TabPanel>

            <TabPanel value={"Read Cluster"} sx={{ padding: 0 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"Start Date"}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    value={
                      typeof readClusterDetail.startDate === "string"
                        ? new Date(readClusterDetail.startDate)
                        : readClusterDetail.startDate
                    }
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        startDate: event || "",
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <DatePickerInput
                    label={"End Date"}
                    value={readClusterDetail?.endDate}
                    placeholder={"dd/mm/yyyy hh:mm"}
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        endDate: event || new Date(),
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                  <CustomOutlinedSelect
                    label={"Register Type Code"}
                    options={registerTypeCodeOptions}
                    placeholder={"Enter or Select a Register Type"}
                    value={readClusterDetail.registerTypeCode}
                    onChange={(event) => {
                      setReadClusterDetail({
                        ...readClusterDetail,
                        registerTypeCode: event as {
                          label: string;
                          value: string;
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 12, lg: 12 }} mt={"10px"}>
                  <ContainedButton
                    label={"Search"}
                    sx={{
                      width: "fit-content",
                      fontWeight: 600,
                      color: "#ffffff",
                    }}
                    onClick={() =>
                      navigate("/read-cluster", {
                        state: {
                          ...readClusterDetail,
                          searchValue: searchValue,
                        },
                      })
                    }
                    disabled={
                      !searchValue ||
                      !readClusterDetail?.startDate ||
                      !readClusterDetail?.endDate ||
                      !readClusterDetail?.registerTypeCode
                    }
                  />
                </Grid>
              </Grid>
            </TabPanel>
          </CardContent>
        </TabContext>
      </Card>

      <AddCustomFieldsDrawer setToast={setToast} />

      <CustomFieldsModal />
    </Layout>
  );
};

export default Meters;
